import type { AxiosError } from 'axios';

import apiClient from './apiClient';
import {
  PAYMENT_POLLING_INTERVAL_MS,
  PAYMENT_POLLING_MAX_ATTEMPTS,
  PAYMENT_RETRY_BUDGET,
} from '../constants/payment';
import {
  mapPaymentToUIState,
} from '../types/payment';
import type {
  Payment,
  PaymentStatus,
  CreatePaymentSessionRequest,
  CreatePaymentSessionResponse,
  CreateStakeSessionResponse,
  StakePaymentSession,
  StakeSessionStatus,
  StakeSessionType,
  GetPaymentStatusResponse,
  PublishTournamentRequest,
  PublishTournamentResponse,
} from '../types/payment';
import { PaymentUIState } from '../types';

export interface PaymentServiceError extends AxiosError {
  httpStatus: number;
  errorCode?: string;
}

export class PaymentPollingTimeoutError extends Error {
  constructor(message = 'Payment polling timed out') {
    super(message);
    this.name = 'PaymentPollingTimeoutError';
  }
}

interface PaymentApiErrorResponse {
  error?: {
    code?: string;
    message?: string;
  };
}

export const mapApiErrorToUIState = (error: unknown): PaymentUIState | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const axiosError = error as PaymentServiceError;

  if (!axiosError?.isAxiosError) {
    return null;
  }

  // Network errors (no response object) should return null
  if (!axiosError.response) {
    return null;
  }

  const status = axiosError.response?.status;
  const data = axiosError.response?.data as PaymentApiErrorResponse | undefined;
  const code = data?.error?.code;

  // Known payment error codes that should be mapped
  const knownPaymentErrorCodes = [
    'ENTRY_FEE_PAYMENT_REQUIRED',
    'ENTRY_FEE_PAYMENT_PENDING',
    'ENTRY_FEE_PAYMENT_EXPIRED',
    'ENTRY_FEE_PAYMENT_MISMATCH',
    'ENTRY_FEE_PAYMENT_ALREADY_USED',
    'ENTRY_FEE_PAYMENT_REFUNDED',
    'ENTRY_FEE_PAYMENT_CHARGEBACKED',
    'PAYMENT_OWNER_MISMATCH',
    'TOURNAMENT_FULL',
    'TOURNAMENT_ALREADY_STARTED',
    'JOIN_NOT_ALLOWED',
    // Solana-specific error codes (data-model-contract §7)
    'SOLANA_AMOUNT_MISMATCH',
    'SOLANA_UNRECOGNIZED_DEPOSITOR_ADDRESS',
    'SOLANA_INSUFFICIENT_CONFIRMATIONS',
    'SOLANA_ADMIN_HOT_WALLET_INSUFFICIENT_SOL',
  ];

  // Standard POL payment HTTP statuses
  const paymentStatuses = [402, 409, 410];

  // Solana codes have specific HTTP statuses that differ from the POL set (data-model-contract §7)
  const solanaStatusMap: Record<string, number> = {
    SOLANA_AMOUNT_MISMATCH: 402,
    SOLANA_INSUFFICIENT_CONFIRMATIONS: 202,
    SOLANA_UNRECOGNIZED_DEPOSITOR_ADDRESS: 412,
    SOLANA_ADMIN_HOT_WALLET_INSUFFICIENT_SOL: 500,
  };

  const isSolanaCode = code != null && Object.prototype.hasOwnProperty.call(solanaStatusMap, code);
  // Solana codes use exact per-code status matching; POL codes use the standard status list
  const isValidStatus = status != null && (
    (isSolanaCode && solanaStatusMap[code!] === status) ||
    (!isSolanaCode && paymentStatuses.includes(status))
  );

  // Only delegate to mapPaymentToUIState for known payment error codes AND valid statuses
  if (!code || !knownPaymentErrorCodes.includes(code) || !isValidStatus) {
    return null;
  }

  // Delegate to mapPaymentToUIState for comprehensive error handling
  // This ensures all payment error codes are handled consistently
  const uiState = mapPaymentToUIState({
    status: 'PENDING', // Default status for error context
    httpStatus: status,
    errorCode: code,
    hasEntryFee: true, // Assume entry fee context for joinTournament errors
  });

  return uiState;
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const TERMINAL_STATUSES: PaymentStatus[] = [
  'CONFIRMED',
  'FAILED',
  'EXPIRED',
  'REFUNDED',
  'CHARGEBACKED',
];

export async function pollPaymentStatus(
  paymentId: string,
  options?: {
    maxAttempts?: number;
    intervalMs?: number;
    timeoutMs?: number;
    onStatusChange?: (status: PaymentStatus) => void;
  },
): Promise<GetPaymentStatusResponse> {
  const {
    maxAttempts = PAYMENT_POLLING_MAX_ATTEMPTS,
    intervalMs = PAYMENT_POLLING_INTERVAL_MS,
    timeoutMs = 120000,
    onStatusChange,
  } = options ?? {};

  let attempts = 0;
  let elapsedMs = 0;
  let lastStatus: PaymentStatus | null = null;

  while (attempts < maxAttempts && elapsedMs <= timeoutMs) {
    attempts += 1;

    const response = await getPaymentStatus(paymentId);
    const currentStatus = response.status;

    if (currentStatus !== lastStatus) {
      onStatusChange?.(currentStatus);
      lastStatus = currentStatus;
    }

    if (TERMINAL_STATUSES.includes(currentStatus)) {
      return response;
    }

    if (attempts >= maxAttempts || elapsedMs + intervalMs > timeoutMs) {
      break;
    }

    await sleep(intervalMs);
    elapsedMs += intervalMs;
  }

  throw new PaymentPollingTimeoutError();
}

/**
 * Retriable HTTP status codes for payment session creation
 */
const RETRIABLE_STATUS_CODES = [500, 502, 503, 504];

/**
 * Backoff delays in milliseconds for retry attempts
 */
const BACKOFF_MS = [1000, 2000, 4000];

/**
 * Create a payment session for tournament entry (organizer payment)
 * @param tournamentId - The tournament ID to create payment for
 * @returns Payment session response with paymentId, amount, currency, etc.
 */
export async function createTournamentEntryPaymentSession(
  tournamentId: string,
): Promise<CreatePaymentSessionResponse> {
  const returnUrl = `${window.location.origin}/payment-qr/{paymentId}`;
  const cancelUrl = `${window.location.origin}/creating-tournament/${tournamentId}`;

  const response = await apiClient.post<CreatePaymentSessionResponse>(
    `/tournaments/${tournamentId}/payment-sessions/join`,
    {
      provider: 'ONCHAIN_POLYGON',
      returnUrl,
      cancelUrl,
    },
  );

  return response.data;
}

/**
 * Create a payment session with automatic retry for retriable errors
 * Implements exponential backoff: 1s, 2s, 4s
 * @param tournamentId - The tournament ID to create payment for
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Payment session response
 * @throws Error if all retries fail or non-retriable error occurs
 */
export async function createTournamentEntryPaymentSessionWithRetry(
  tournamentId: string,
  maxRetries: number = PAYMENT_RETRY_BUDGET,
): Promise<CreatePaymentSessionResponse> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await createTournamentEntryPaymentSession(tournamentId);
    } catch (error) {
      lastError = error as Error;
      const axiosError = error as PaymentServiceError;

      // Check if retriable
      const status = axiosError.response?.status;
      const isRetriable =
        !status || RETRIABLE_STATUS_CODES.includes(status) || !axiosError.response;

      if (!isRetriable || attempt >= maxRetries - 1) {
        throw error;
      }

      // Wait before retry with exponential backoff
      await sleep(BACKOFF_MS[attempt] || BACKOFF_MS[BACKOFF_MS.length - 1]);
    }
  }

  throw lastError;
}

/**
 * Create a payment session for tournament join entry (player payment)
 * @param tournamentId - The tournament ID to join
 * @returns Payment session response with paymentId, amount, currency, etc.
 */
export async function createJoinPaymentSession(
  tournamentId: string,
): Promise<CreatePaymentSessionResponse> {
  const returnUrl = `${window.location.origin}/payment-qr/{paymentId}`;
  const cancelUrl = `${window.location.origin}/tournament/${tournamentId}`;

  const response = await apiClient.post<CreatePaymentSessionResponse>(
    `/tournaments/${tournamentId}/payment-sessions/join`,
    {
      provider: 'ONCHAIN_POLYGON',
      returnUrl,
      cancelUrl,
    },
  );

  return response.data;
}

/**
 * Create a join payment session with automatic retry for retriable errors
 * Implements exponential backoff: 1s, 2s, 4s
 * @param tournamentId - The tournament ID to join
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Payment session response
 * @throws Error if all retries fail or non-retriable error occurs
 */
export async function createJoinPaymentSessionWithRetry(
  tournamentId: string,
  maxRetries: number = PAYMENT_RETRY_BUDGET,
): Promise<CreatePaymentSessionResponse> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await createJoinPaymentSession(tournamentId);
    } catch (error) {
      lastError = error as Error;
      const axiosError = error as PaymentServiceError;

      // Check if retriable
      const status = axiosError.response?.status;
      const isRetriable =
        !status || RETRIABLE_STATUS_CODES.includes(status) || !axiosError.response;

      if (!isRetriable || attempt >= maxRetries - 1) {
        throw error;
      }

      // Wait before retry with exponential backoff
      await sleep(BACKOFF_MS[attempt] || BACKOFF_MS[BACKOFF_MS.length - 1]);
    }
  }

  throw lastError;
}

export async function getPaymentStatus(
  paymentId: string,
): Promise<GetPaymentStatusResponse> {
  if (!paymentId) {
    throw new Error('paymentId is required');
  }

  try {
    const response = await apiClient.get<GetPaymentStatusResponse>(
      `/payment-sessions/${paymentId}`,
    );

    return response.data;
  } catch (error) {
    const typedError = error as PaymentServiceError;
    throw typedError?.response?.data ?? typedError?.message ?? error;
  }
}

/**
 * Normalises a raw stake-payment-session payload into the camelCase
 * `StakePaymentSession` shape the rest of the frontend depends on.
 *
 * Defensive unwrapping: backend custom controllers (`POST
 * /tournaments/:id/payment-sessions/join`, `GET /payment-sessions/:id`)
 * use `ctx.send({ data: payload })`, so axios surfaces `response.data` as
 * `{ data: { id, status, ... } }`. Reading `d.expectedAmount` on the
 * outer object silently produced `undefined`, which `safeNum` clamped to
 * `0` and `as` casts left as `undefined` — that propagated all the way
 * to the verify screen rendering empty currency / amount even though
 * the backend response was correct. We now look one level deeper when
 * the outer object exposes a `data` object that looks like a session.
 *
 * Both REST endpoints share this shape, so a single normaliser keeps
 * `getStakeSessionStatus` (polling) and `createStakePaymentSession`
 * (initial hydration) in sync.
 */
function normalizeStakePaymentSession(raw: unknown): StakePaymentSession {
  const root = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const inner = (root.data && typeof root.data === 'object' && !Array.isArray(root.data))
    ? root.data as Record<string, unknown>
    : null;
  // Prefer the inner object only when it actually carries session fields,
  // so a backend that ever stops wrapping won't silently break.
  const d = (inner && ('id' in inner || 'expectedAmount' in inner || 'expected_amount' in inner))
    ? inner
    : root;

  const safeNum = (v: unknown, fallback: number): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const rawRequired = d.paymentRequired ?? d.payment_required;

  return {
    id: safeNum(d.id, 0),
    status: (d.status as StakeSessionStatus),
    sessionType: ((d.sessionType ?? d.session_type ?? 'JOIN') as StakeSessionType),
    expectedAmount: safeNum(d.expectedAmount ?? d.expected_amount, 0),
    paidAmount: safeNum(d.paidAmount ?? d.paid_amount, 0),
    paymentDeadline: String(d.paymentDeadline ?? d.payment_deadline ?? ''),
    paymentRequired: rawRequired === undefined
      ? true
      : rawRequired === true || rawRequired === 'true' || rawRequired === 1 || rawRequired === '1',
    depositAddress: (d.depositAddress ?? d.deposit_address) as string | undefined,
    tournament: safeNum(d.tournament, 0),
    sessionState: d.sessionState as StakePaymentSession['sessionState'],
    alreadyJoined: typeof d.alreadyJoined === 'boolean' ? d.alreadyJoined : undefined,
    // TB-RULE-SOCKET-REST-FALLBACK: polling must surface chain fields so
    // socket-dropped sessions preserve chain-aware rendering.
    stakeCurrency: d.stakeCurrency as StakePaymentSession['stakeCurrency'],
    networkInfo: d.networkInfo as StakePaymentSession['networkInfo'],
  };
}

/**
 * Get stake payment session status by session ID.
 * Used as polling fallback when socket events don't arrive.
 * Backend returns snake_case — we map to camelCase StakePaymentSession.
 */
export async function getStakeSessionStatus(
  sessionId: number,
): Promise<StakePaymentSession> {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  const response = await apiClient.get<Record<string, unknown>>(
    `/payment-sessions/${sessionId}`,
  );

  return normalizeStakePaymentSession(response.data);
}

/**
 * Create a stake payment session for unified tournament payments.
 * Works for both owner (CREATE) and joiner (JOIN) - backend determines session_type.
 *
 * @param tournamentId - The tournament ID
 * @returns Stake payment session with id, status, expectedAmount, paidAmount, etc.
 */
export async function createStakePaymentSession(
  tournamentId: string,
): Promise<CreateStakeSessionResponse> {
  const response = await apiClient.post<Record<string, unknown>>(
    `/tournaments/${tournamentId}/payment-sessions/join`,
    {},
  );

  return { data: normalizeStakePaymentSession(response.data) };
}

/**
 * Create a stake payment session with automatic retry for retriable errors.
 * Implements exponential backoff: 1s, 2s, 4s
 * 
 * @param tournamentId - The tournament ID
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Stake payment session
 * @throws Error if all retries fail or non-retriable error occurs
 */
export async function createStakePaymentSessionWithRetry(
  tournamentId: string,
  maxRetries: number = PAYMENT_RETRY_BUDGET,
): Promise<CreateStakeSessionResponse> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await createStakePaymentSession(tournamentId);
    } catch (error) {
      lastError = error as Error;
      const axiosError = error as PaymentServiceError;

      const status = axiosError.response?.status;
      const isRetriable =
        !status || RETRIABLE_STATUS_CODES.includes(status) || !axiosError.response;

      if (!isRetriable || attempt >= maxRetries - 1) {
        throw error;
      }

      await sleep(BACKOFF_MS[attempt] || BACKOFF_MS[BACKOFF_MS.length - 1]);
    }
  }

  throw lastError;
}

export async function publishTournament(
  tournamentId: string,
  paymentId: string,
): Promise<PublishTournamentResponse> {
  try {
    const response = await apiClient.post<PublishTournamentResponse>(
      `/tournaments/${tournamentId}/publish`,
      { paymentId },
    );

    return response.data;
  } catch (error) {
    const typedError = error as PaymentServiceError;
    throw typedError?.response?.data ?? typedError?.message ?? error;
  }
}
